import { useTranslation } from 'react-i18next';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

export interface AccountsPaginationProps {
    page: number;
    totalPages: number;
    pageSize: number;
    onPageSizeChange: (size: number) => void;
    rangeFrom: number;
    rangeTo: number;
    total: number;
    onGoTo: (page: number) => void;
    pageSizeOptions?: number[];
}

/**
 * EAA-1820: the ONE pager, replacing the realm's `accounts/AccountsPagination.tsx`
 * (paginates an already-scanned, client-filtered set) and the org's
 * `hierarchy/scopedAccounts/ScopedAccountsPagination.tsx` (paginates real server
 * `page`/`page_size` params). Both now drive real server pages (see
 * `useRealmAccountsAdapter`), so the two are the same component with the same
 * inputs — `rangeFrom`/`rangeTo`/`total` are computed by each adapter from
 * whatever it actually knows server-side.
 */
export function AccountsPagination({
    page,
    totalPages,
    pageSize,
    onPageSizeChange,
    rangeFrom,
    rangeTo,
    total,
    onGoTo,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
}: AccountsPaginationProps) {
    const { t } = useTranslation();

    // Small page-count window: current ±2, always showing first/last.
    const pages = new Set<number>([1, totalPages, page - 2, page - 1, page, page + 1, page + 2]);
    const pageList = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

    return (
        <div className="portal-console-pagination portal-accounts-pagination">
            <span>
                {t('console.accounts.pagination.summary', {
                    from: rangeFrom,
                    to: rangeTo,
                    total: total.toLocaleString('vi-VN'),
                })}
            </span>
            <span className="portal-accounts-pagination-spacer" />
            <label className="portal-accounts-page-size">
                {t('console.accounts.pagination.pageSizeLabel')}
                <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
                    {pageSizeOptions.map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </select>
            </label>
            <div className="portal-audit-pager">
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onGoTo(1)}
                    aria-label={t('console.accounts.pagination.firstPageAriaLabel')}
                >
                    «
                </button>
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onGoTo(page - 1)}
                    aria-label={t('console.accounts.pagination.previousPageAriaLabel')}
                >
                    ‹
                </button>
                {pageList.map((p) => (
                    <button
                        key={p}
                        type="button"
                        className={p === page ? 'is-current' : undefined}
                        onClick={() => onGoTo(p)}
                        aria-current={p === page ? 'page' : undefined}
                    >
                        {p}
                    </button>
                ))}
                <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => onGoTo(page + 1)}
                    aria-label={t('console.accounts.pagination.nextPageAriaLabel')}
                >
                    ›
                </button>
                <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => onGoTo(totalPages)}
                    aria-label={t('console.accounts.pagination.lastPageAriaLabel')}
                >
                    »
                </button>
            </div>
        </div>
    );
}

export default AccountsPagination;
